/**
 * Maps top-level navigation routes to their lazy module loaders. Calling a
 * loader triggers Vite to fetch + parse the chunk in the background, so when
 * the user actually clicks the link the route renders without a network round
 * trip. Used by the Sidebar on hover/focus.
 *
 * Only the most-visited routes are wired here — adding more increases sidebar
 * import surface for marginal benefit.
 */
type ChunkLoader = () => Promise<unknown>;

const map: Record<string, ChunkLoader> = {
  '/dashboard': () => import('@features/dashboard/DashboardPage'),
  '/staff': () => import('@features/staffManagement/pages/StaffOverview'),
  '/work-time': () => import('@features/workTime/pages/WorkTimeOverview'),
  '/devices': () => import('@features/devices/DevicesPage'),
  '/reports': () => import('@features/reports/pages/ReportsOverview'),
  '/reports/attendance': () => import('@features/reports/pages/MasterAttendanceReportPage'),
  '/settings': () => import('@features/settings/pages/SettingsOverview'),
  '/audit-log': () => import('@features/auditLog/AuditLogPage'),
  '/attendance-management': () =>
    import('@features/attendanceManagement/pages/AttendanceManagementOverview'),
};

const inflight = new Set<string>();

export function prefetchRoute(path: string): void {
  if (inflight.has(path)) return;
  const loader = map[path];
  if (!loader) return;
  inflight.add(path);
  // Fire-and-forget; surface failures only in dev.
  loader().catch((e) => {
    inflight.delete(path);
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.warn(`[prefetch] failed for ${path}`, e);
    }
  });
}
